import React, { useCallback } from "react";
import type { IframeScenario } from "./settings/iframes-mapping";
import { useIframeStylesHook } from "./hooks/useIframeStylesHook";
import { useIframePerfEvents } from "./hooks/useIframePerfEvents";
import { useIframeState } from "./hooks/useIframeState";
import { useIframeMemoryManagement } from "./hooks/useIframeMemoryManagement";
import { useIframeSettings } from "./hooks/useIframeSettings";

type OWAIframeProps = React.IframeHTMLAttributes<HTMLIFrameElement> & {
    isVisible: boolean;
    scenario: IframeScenario;
};

/**
 * This component should be use instead of the "iframe" component. It's a drop-in replacement
 * but allows to manage the iframe view state and track the iframe's performance effects.
 * For scenario where we don't render the iframe ourselves, we should use "OWAIframeWrapper".
 * --> See README.md for more information.
 */
const OWAIframeInternal: React.FC<OWAIframeProps> = ({ isVisible, scenario, style, ...props }) => {
    const settings = useIframeSettings(scenario);
    const { enablePreloading, enableCaching } = settings;
    const { getPerfEvent } = useIframePerfEvents("inline", scenario);
    const { dpLoadEnd, dpEndEvent, dpLoadStart } = getPerfEvent(`iframe-${scenario}`);
    const { value: state, shouldRender, isVisibleToUser, } = useIframeState(scenario, isVisible, settings, () => dpLoadStart("initial"));
    const { doNotRenderCachedIframe } = useIframeMemoryManagement(state, scenario, isVisible, enableCaching);
    
    // Manage the visibility of the iframe based on the hidden prop
    const iframeStyle = useIframeStylesHook(isVisibleToUser, style);
    
    const handleOnLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>) => {
        dpLoadEnd(event.currentTarget.src, state, enablePreloading);
        
        // If the props.onLoad function is provided, call it
        if (props.onLoad) {
            props.onLoad(event);
        }
        
        dpEndEvent();
    }, [props.onLoad, state]);
    
    // Only render the iframe if it has been mounted (became visible)
    return shouldRender && !doNotRenderCachedIframe ? (<iframe {...props} onLoad={handleOnLoad} style={iframeStyle}/>) : null;
};

// Avoid unnecessary re-renders if the props haven't changed
export const OWAIframe: React.FC<OWAIframeProps> = React.memo(OWAIframeInternal);
